import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Case } from '@components/conditional';
import { GuideCoursePage } from '@course/components/guide-course-page';
import { HeadingPrimary } from '@course/components/headings';
import { BtnSecondary } from '@components/buttons';
import { LoadedProps } from '@components/router';
import { defCoursesRoute } from '@course/components/courses-app-router';
import { load, State } from './state';
import { meetingUrl } from '../guide-course-meetings/urls';
import type { MeetingSeriesSessionRow } from 'server/dbx/meeting-series';
import { SessionActions } from './session-actions';
import { SeriesManagement } from './series-management';
import { TopicCell } from './topic-cell';
import { rpx } from 'client/lib/rpx-client';

dayjs.extend(utc);
dayjs.extend(timezone);

export const route = defCoursesRoute({
  Page,
  load,
  authLevel: 'guide',
  key: ({ params }) => `meeting_series_${params.seriesId}`,
});

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function Page(props: LoadedProps<typeof load>) {
  const { course, series, sessions } = props.state;

  async function reload() {
    const data = await rpx.meetingSeries.getMeetingSeries({
      courseId: course.id,
      seriesId: series.id,
    });
    props.setState((s) => ({ ...s, series: data.series, sessions: data.sessions }));
  }

  return (
    <GuideCoursePage
      course={course}
      isLoading={!course}
      documentTitle={series.title}
      type="meetings"
    >
      <div class="flex flex-col grow p-4 md:p-8 max-w-5xl mx-auto w-full">
        <SeriesHeader series={series} sessions={sessions} courseId={course.id} onChanged={reload} />
        <SessionList
          sessions={sessions}
          courseId={course.id}
          timezone={series.timezone}
          endedEarlyAt={series.endedEarlyAt}
          onChanged={reload}
        />
      </div>
    </GuideCoursePage>
  );
}

interface SeriesHeaderProps {
  series: State['series'];
  sessions: State['sessions'];
  courseId: UUID;
  onChanged(): void;
}

function SeriesHeader({ series, sessions, courseId, onChanged }: SeriesHeaderProps) {
  const cadenceLabel = series.cadence === 'weekly' ? 'Weekly' : 'Every other week';
  const dayLabel = DAY_LABELS[series.dayOfWeek] ?? '';
  const timeLabel = formatTimeOfDay(series.timeOfDay);
  const dateRange = `${formatDate(series.startDate)} → ${formatDate(series.endDate)}`;
  const total = sessions.length;
  const skipped = sessions.filter((s) => s.disruption === 'skipped').length;
  const cancelled = sessions.filter((s) => s.disruption === 'cancelled').length;
  const endedEarly = series.endedEarlyAt
    ? sessions.filter(
        (s) =>
          !s.disruption &&
          s.scheduledAt &&
          dayjs(s.scheduledAt).isAfter(dayjs(series.endedEarlyAt)),
      ).length
    : 0;
  const active = total - skipped - cancelled - endedEarly;
  const status = series.endedEarlyAt ? 'Ended early' : 'Active';
  const sessionsLabel = [
    `${active} active`,
    skipped && `${skipped} skipped`,
    cancelled && `${cancelled} cancelled`,
    endedEarly && `${endedEarly} ended early`,
  ]
    .filter(Boolean)
    .join(' • ');

  return (
    <header class="mb-8">
      <div class="flex items-start justify-between gap-4 mb-2 flex-wrap">
        <HeadingPrimary title={series.title} />
        <div class="flex items-center gap-2 flex-wrap">
          <SeriesManagement series={series} onChanged={onChanged} />
          <BtnSecondary href={meetingUrl({ courseId })}>Back to meetings</BtnSecondary>
        </div>
      </div>
      <p class="text-gray-600 dark:text-gray-400 mb-4">
        Calendar title: <span class="font-medium">{series.calendarTitle}</span>
      </p>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Schedule" value={`${cadenceLabel} • ${dayLabel}`} />
        <Stat label="Time" value={`${timeLabel} ${series.timezone}`} />
        <Stat label="Dates" value={dateRange} />
        <Stat label="Sessions" value={sessionsLabel} />
      </div>
      {!series.bridgeLink && (
        <div class="mt-4 p-3 rounded bg-amber-50 dark:bg-amber-900/30 text-sm text-amber-900 dark:text-amber-200">
          No bridge link set — calendar invites are paused until you add one.
        </div>
      )}
      {series.endedEarlyAt && (
        <div class="mt-4 p-3 rounded bg-gray-100 dark:bg-gray-800 text-sm">
          Series ended early on{' '}
          {dayjs(series.endedEarlyAt).tz(series.timezone).format('MMM D, YYYY')}.
        </div>
      )}
      <div class="mt-4 text-xs text-gray-500">Status: {status}</div>
    </header>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div class="border rounded-lg p-3 bg-white dark:bg-gray-900">
      <div class="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div class="mt-1 text-sm font-medium">{value}</div>
    </div>
  );
}

interface SessionListProps {
  sessions: MeetingSeriesSessionRow[];
  courseId: UUID;
  timezone: string;
  endedEarlyAt?: Date;
  onChanged(): void;
}

function SessionList({ sessions, courseId, timezone, endedEarlyAt, onChanged }: SessionListProps) {
  return (
    <section>
      <h2 class="text-lg font-semibold mb-3">Sessions</h2>
      <Case
        when={sessions.length > 0}
        fallback={<p class="text-sm text-gray-500">No sessions yet.</p>}
      >
        <div class="border rounded-lg overflow-hidden bg-white dark:bg-gray-900">
          <table class="w-full text-sm" aria-label="Series sessions">
            <thead class="bg-gray-50 dark:bg-gray-800 text-left">
              <tr>
                <th class="px-3 py-2 w-12">#</th>
                <th class="px-3 py-2">Date</th>
                <th class="px-3 py-2">Topic</th>
                <th class="px-3 py-2 w-32">Status</th>
                <th class="px-3 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <SessionRow
                  key={s.id}
                  session={s}
                  courseId={courseId}
                  timezone={timezone}
                  endedEarlyAt={endedEarlyAt}
                  onChanged={onChanged}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Case>
    </section>
  );
}

interface SessionRowProps {
  session: MeetingSeriesSessionRow;
  courseId: UUID;
  timezone: string;
  endedEarlyAt?: Date;
  onChanged(): void;
}

function SessionRow({ session, courseId, timezone, endedEarlyAt, onChanged }: SessionRowProps) {
  const status = sessionStatus(session, endedEarlyAt);
  const sessionDate = session.scheduledAt ? dayjs(session.scheduledAt).tz(timezone) : undefined;
  const origDate = session.originalStartsAt
    ? dayjs(session.originalStartsAt).tz(timezone)
    : undefined;
  const origFormat =
    origDate && sessionDate && origDate.year() !== sessionDate.year()
      ? 'MMM D, YYYY h:mm A'
      : 'MMM D h:mm A';
  return (
    <tr class="border-t">
      <td class="px-3 py-2 text-gray-500 align-top">{session.seriesOccurrenceIndex + 1}</td>
      <td class="px-3 py-2 align-top whitespace-nowrap">
        {sessionDate ? sessionDate.format('ddd, MMM D, YYYY h:mm A') : '—'}
        {origDate && (
          <div class="text-xs text-gray-500 mt-0.5">
            Rescheduled from {origDate.format(origFormat)}
          </div>
        )}
      </td>
      <td class="px-3 py-2 align-top min-w-[18rem]">
        <TopicCell
          courseId={courseId}
          sessionId={session.id}
          value={session.title || ''}
          isCustomized={session.isTopicCustomized}
          isDisrupted={!!session.disruption}
          onSaved={onChanged}
        />
        {session.disruption === 'skipped' && session.disruptionReason && (
          <div class="text-xs text-gray-500 mt-1">Skipped: {session.disruptionReason}</div>
        )}
      </td>
      <td class="px-3 py-2 align-top">
        <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
      </td>
      <td class="px-3 py-2 align-top text-right whitespace-nowrap">
        <SessionActions
          courseId={courseId}
          session={session}
          seriesTimezone={timezone}
          onChanged={onChanged}
        />
        <a
          href={meetingUrl({ courseId, meetingId: session.id })}
          class="ml-3 text-xs text-gray-600 hover:underline"
        >
          Open
        </a>
      </td>
    </tr>
  );
}

type Tone = 'gray' | 'green' | 'amber' | 'red';

function StatusBadge({ tone, children }: { tone: Tone; children: any }) {
  const tones: Record<Tone, string> = {
    gray: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    green: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    red: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  };
  return (
    <span class={`inline-block px-2 py-0.5 rounded text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

function sessionStatus(
  s: MeetingSeriesSessionRow,
  endedEarlyAt?: Date,
): { label: string; tone: Tone } {
  if (s.disruption === 'cancelled') {
    return { label: 'Cancelled', tone: 'red' };
  }
  if (s.disruption === 'skipped') {
    return { label: 'Skipped', tone: 'amber' };
  }
  if (s.scheduledAt && endedEarlyAt && dayjs(s.scheduledAt).isAfter(dayjs(endedEarlyAt))) {
    return { label: 'Ended early', tone: 'red' };
  }
  if (s.scheduledAt) {
    const start = dayjs(s.scheduledAt);
    const end = start.add(s.durationMinutes, 'minute');
    const now = dayjs();
    if (now.isBefore(start)) {
      return { label: 'Scheduled', tone: 'green' };
    }
    if (now.isBefore(end)) {
      return { label: 'In progress', tone: 'amber' };
    }
    return { label: 'Past', tone: 'gray' };
  }
  return { label: 'Scheduled', tone: 'green' };
}

function formatTimeOfDay(t: string): string {
  const [h, m] = t.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) {
    return t;
  }
  const period = h >= 12 ? 'PM' : 'AM';
  const hh = h % 12 || 12;
  return `${hh}:${String(m).padStart(2, '0')} ${period}`;
}

function formatDate(d: Date | string | undefined): string {
  if (!d) {
    return '';
  }
  return dayjs.utc(d).format('MMM D, YYYY');
}
